import { useEffect } from 'react';
import { browserHistory } from '@americanexpress/one-app-router';
import { getNewSearchString } from '../utils/util';
import { extractMapResponseValueByKey } from '../utils/conditionEvaluator';

const usePageNavigation = ({
  elementContainer,
  formContainer,
  objectOfFunctions,
  loggerApiCall,
  setCurPageValues,
  setRenderingEl,
  dynamicPropertyEvaluationForContainer,
  track,
  scrollTop,
  operationCallerRef,
}) => {
  const getContainerIndex = (page) => {
    let pageIndex = -1;
    elementContainer.forEach((row, index) => {
      if (row.page.toString() === page.toString()) {
        pageIndex = index;
      }
    });
    return pageIndex;
  };

  const handleContinue = async (isNextPage = 0, eventType = 'continueButton') => {
    setCurPageValues({});
    let currentPage;
    let nextPage;

    if (isNextPage !== 0) {
      nextPage = isNextPage;
    }

    let pageValue = '';
    /**
     * Get the current page number/name
     */
    /* eslint-disable no-restricted-syntax -- need for-of */
    for (const container of elementContainer) {
      if (container.page && container.isPageVisited) {
        currentPage = container.page;
        if (eventType === 'continueButton'
          && formContainer.operations?.onPageNavigate?.[currentPage]
          && operationCallerRef.current) {
          operationCallerRef.current(formContainer.operations.onPageNavigate[currentPage]);
        }
        /**
         * Get the next page number/name if not already provided
         * If nextPageDynamicEvaluation flag is set in JSON, then evaluate the next page number
         * Else, get the next page number from JSON
         * If nextPage is not provided in JSON, then increment the current page number by 1
         */
        if (!nextPage) {
          if (container.nextPageDynamicEvaluation) {
            /* nextPageDynamicEvaluation flag is set in JSON */
            nextPage = extractMapResponseValueByKey(
              container,
              'nextPageDynamicEvaluation',
              objectOfFunctions,
              formContainer
            );
          } else if (container.nextPage) {
            nextPage = container.nextPage;
          } else {
            nextPage = currentPage + 1;
          }
        }
        container.isPageVisited = false;
        container.isFieldsVisited = true;
      }
    }
    let logString = '';
    if (eventType === 'continueButton' || eventType === 'backButton') {
      logString = `event=${eventType}Click currentPage=${currentPage} nextPage=${nextPage}`;
      loggerApiCall(logString, 'info', { eventType, currentPage, nextPage });
    }
    /* eslint-enable no-restricted-syntax -- need for-of */

    if (nextPage) {
      // if nextPage is thankyou page --> update this information in current pages's history state
      if (nextPage === 'thankyou'
        || (formContainer?.operations?.preventLoadLastPageFromSuccessPage
          && formContainer?.operations?.preventLoadLastPageFromSuccessPage
            .indexOf(nextPage) !== -1)) {
        window.history.replaceState({ nextPageIs: 'thankyou' }, '', window.location.href);
      }
      const nextPageIndex = getContainerIndex(nextPage);
      if (nextPageIndex !== -1) {
        let newContainer = elementContainer[nextPageIndex];
        newContainer = dynamicPropertyEvaluationForContainer(newContainer);
        setRenderingEl(newContainer);
        newContainer.isPageVisited = true;
        logString = `page=${nextPage}`;
        loggerApiCall(logString, 'info');
        const searchString = getNewSearchString(nextPage);
        const updatedUrl = `${window.location.pathname}${searchString}`;
        browserHistory.push(updatedUrl);
        /* page tracking in between pages */
        if (newContainer.trackingName) {
          pageValue = newContainer.trackingName;
        } else {
          pageValue = `page ${nextPage}`;
        }
        track({
          event: {
            element: `On ${pageValue} form load`,
            detail: `${pageValue} visited`,
            type: 'impression',
            identifier: 'iForms',
            pageName: `${pageValue}`,
            meta: {
              page: {
                pageInfo: {
                  pageName: `${pageValue}`,
                },
              },
            },
          },
        });
      }
      scrollTop();
      return false;
    }

    logString = 'status=invalidNextPageNumber';
    loggerApiCall(logString, 'error');
    return false;
  };

  useEffect(() => {
    const handlePopState = (state) => {
      if (state?.state?.nextPageIs) {
        // user coming from thankyou page and we restart the journey -- default
        window.history.forward();
        if (!formContainer?.operations?.restrictOnBackFromSubmit) {
          window.location.reload();
        }
      } else {
        // user not coming from thankyou page --> default back behaviour
        window.history.scrollRestoration = 'manual';
        const urlSearchParams = new URLSearchParams(window.location.search);
        const params = Object.fromEntries(urlSearchParams.entries());
        handleContinue(params.page, 'backButton');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  return { handleContinue, getContainerIndex };
};

export default usePageNavigation;
